import { computed, unref, type Ref } from "vue";
import { useQuery } from "@tanstack/vue-query";
import Opportunity from "@/services/opportunity/opportunity";
import type { GetOpportunity } from "@/dtos/Opportunity.dto";

export interface OpportunityParams {
  id: string;
  organizationId: string;
}

function isRef<T>(param: Ref<T> | T): param is Ref<T> {
  return typeof param === "object" && param !== null && "value" in param;
}

export function useOpportunity(params: Ref<OpportunityParams | null> | OpportunityParams | null) {
  const queryKey = computed(() => {
    const p = isRef(params) ? params.value : params;
    if (!p) return ["opportunity", "disabled"];
    return ["opportunity", p.id, p.organizationId];
  });

  const enabled = computed(() => {
    const p = isRef(params) ? params.value : params;
    return p !== null && p !== undefined && !!p.id && !!p.organizationId;
  });

  return useQuery({
    queryKey,
    queryFn: async () => {
      const p = isRef(params) ? params.value : params;
      if (!p) throw new Error("Parameters not ready");

      const queryParams: GetOpportunity = {
        id: p.id,
        organizationId: p.organizationId,
      };

      const result = await Opportunity.getOpportunities(queryParams);

      if (result.data.status) {
        const opportunities = result.data.data;
        return opportunities && opportunities.length > 0 ? opportunities[0] : null;
      }

      throw new Error(result.data.msg || "Failed to load opportunity");
    },
    enabled,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });
}
